import React, { useEffect, useState } from "react";
import { Syringe, X, Trash2, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/pages/bitepal/Home";

const MEDS = ["Ozempic", "Wegovy", "Mounjaro", "Zepbound", "Other"];
const DOSES = [0.25, 0.5, 1.0, 1.7, 2.4];
const STATUS = { overused: "#F43F5E", caution: "#F59E0B", rested: "#651F33" };
// zone -> {cx,cy} on 220x360 front-body viewBox
const ZONES = {
  abdomen_left: { cx: 128, cy: 176 }, abdomen_right: { cx: 92, cy: 176 },
  thigh_left: { cx: 130, cy: 250 }, thigh_right: { cx: 90, cy: 250 },
  arm_left: { cx: 172, cy: 150 }, arm_right: { cx: 48, cy: 150 },
};

export default function Injections() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => {
    try { const [s, h] = await Promise.all([api("/injections/sites"), api("/injections")]); setData(s); setHistory(h); }
    catch { setData({ sites: [], suggested_site: null }); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const byId = Object.fromEntries(data.sites.map((s) => [s.site, s]));
  const suggested = byId[data.suggested_site];

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-1 flex items-center gap-2"><Syringe className="text-[#651F33]" /> Injections</h1>
      <p className="text-slate-500 text-sm mb-5">GLP-1 dose log & smart site rotation</p>

      {suggested && (
        <div className="mb-4 p-4 rounded-2xl bg-[#651F33]/10 border border-[#651F33]/30 flex items-center gap-3">
          <MapPin className="text-[#651F33]" size={20} />
          <div><p className="font-bold text-slate-900">Next suggested site: {suggested.label}</p>
            <p className="text-xs text-slate-500">{suggested.days_since == null ? "Never used — freshest spot!" : `Rested ${suggested.days_since} days`}</p></div>
          <button onClick={() => setSel(data.suggested_site)} className="ml-auto bg-[#651F33] text-[#F5F0E8] font-bold text-sm px-4 py-2 rounded-full">Log here</button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="flex flex-col items-center">
          <p className="font-bold text-slate-900 mb-2">Rotation Map</p>
          <svg width="220" height="360" viewBox="0 0 220 360">
            <ellipse cx="110" cy="34" rx="22" ry="24" fill="#E2E8F0" />
            <rect x="74" y="60" width="72" height="110" rx="24" fill="#E2E8F0" />
            <rect x="30" y="70" width="26" height="100" rx="13" fill="#E2E8F0" />
            <rect x="164" y="70" width="26" height="100" rx="13" fill="#E2E8F0" />
            <rect x="80" y="200" width="26" height="120" rx="13" fill="#E2E8F0" />
            <rect x="114" y="200" width="26" height="120" rx="13" fill="#E2E8F0" />
            {Object.entries(ZONES).map(([id, p]) => {
              const s = byId[id]; if (!s) return null;
              const color = STATUS[s.status];
              const isSug = id === data.suggested_site;
              return (
                <g key={id} onClick={() => setSel(id)} style={{ cursor: "pointer" }}>
                  {isSug && <circle cx={p.cx} cy={p.cy} r={18} fill="none" stroke="#651F33" strokeWidth={2} strokeDasharray="4 3" />}
                  <circle cx={p.cx} cy={p.cy} r={13} fill={color} opacity={0.9} />
                </g>
              );
            })}
          </svg>
          <div className="flex gap-4 mt-2 text-xs">
            {Object.entries(STATUS).map(([k, c]) => <span key={k} className="flex items-center gap-1 text-slate-500"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} /> {k}</span>)}
          </div>
        </Card>

        <div className="flex flex-col gap-2">
          {data.sites.map((s) => (
            <button key={s.site} data-testid={`inj-site-${s.site}`} onClick={() => setSel(s.site)}
              className="flex items-center justify-between bg-white border border-[#E3D9C8] rounded-2xl px-4 py-3 hover:border-[#651F33]/50">
              <div className="flex items-center gap-3"><span className="w-3 h-3 rounded-full" style={{ background: STATUS[s.status] }} />
                <span className="font-semibold text-slate-900">{s.label}</span></div>
              <span className="text-xs text-slate-500">{s.days_since == null ? "Never used" : `${s.days_since}d ago`}{s.status === "overused" ? " · overused" : ""}</span>
            </button>
          ))}
        </div>
      </div>

      <h2 className="font-bold text-lg mt-7 mb-2 text-slate-900">Recent Injections</h2>
      {history.length === 0 ? <Card><p className="text-slate-500">No injections logged yet.</p></Card> :
        history.map((h) => (
          <div key={h.injection_id} className="flex items-center gap-3 bg-white border border-[#E3D9C8] rounded-2xl p-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#651F33]/15 flex items-center justify-center"><Syringe size={16} className="text-[#651F33]" /></div>
            <div className="flex-1"><p className="font-semibold text-slate-900">{h.medication} · {h.dose_mg} mg</p>
              <p className="text-xs text-slate-500">{h.site_label} · {new Date(h.injected_at).toLocaleDateString()}</p></div>
            <button onClick={async () => { await api(`/injections/${h.injection_id}`, { method: "DELETE" }); load(); }} className="text-slate-400 hover:text-[#F43F5E]"><Trash2 size={18} /></button>
          </div>
        ))}

      {sel && <LogModal site={byId[sel]} onClose={() => setSel(null)} onSaved={load} />}
    </div>
  );
}

function LogModal({ site, onClose, onSaved }) {
  const [med, setMed] = useState("Ozempic");
  const [dose, setDose] = useState(0.5);
  const [saving, setSaving] = useState(false);
  const overused = site.status === "overused";

  const save = async () => {
    setSaving(true);
    try { const r = await api("/injections", { method: "POST", body: { medication: med, dose_mg: dose, site: site.site } });
      if (r.warning) toast.warning(r.warning); else toast.success("Injection logged! +15 XP");
      onSaved(); onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#E3D9C8] rounded-3xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3"><h2 className="text-lg font-extrabold text-slate-900">Log injection — {site.label}</h2><button onClick={onClose}><X className="text-slate-500" /></button></div>
        {overused && <div className="mb-3 p-3 rounded-xl bg-[#F43F5E]/10 border border-[#F43F5E]/30 flex items-center gap-2 text-sm text-[#F43F5E] font-semibold"><AlertTriangle size={16} /> Used within 7 days — consider a rested site.</div>}
        <p className="text-sm text-slate-500 font-semibold mb-2">Medication</p>
        <div className="flex flex-wrap gap-2">{MEDS.map((m) => <button key={m} onClick={() => setMed(m)} className={`px-3 py-2 rounded-full text-sm font-semibold border ${med === m ? "bg-[#651F33] border-[#651F33] text-[#F5F0E8]" : "bg-white border-[#E3D9C8] text-slate-500"}`}>{m}</button>)}</div>
        <p className="text-sm text-slate-500 font-semibold mt-4 mb-2">Dose (mg)</p>
        <div className="flex flex-wrap gap-2">{DOSES.map((d) => <button key={d} onClick={() => setDose(d)} className={`px-3 py-2 rounded-full text-sm font-semibold border ${dose === d ? "bg-[#651F33] border-[#651F33] text-[#F5F0E8]" : "bg-white border-[#E3D9C8] text-slate-500"}`}>{d}</button>)}</div>
        <button data-testid="inj-save-button" onClick={save} disabled={saving} className="w-full mt-6 py-3.5 rounded-full bg-[#651F33] text-[#F5F0E8] font-bold disabled:opacity-60">{saving ? "Saving…" : "Log injection"}</button>
      </div>
    </div>
  );
}
